import {ForwardedRef, useEffect, useMemo, useRef} from 'react';
import {FocusManagementType} from './types';
import {useFocusTrap} from './useFocusTrap';
import {usePopoverStack} from './usePopoverStack';

/**
 * Hook to add a popover to the focus management stack, and add the focus trap to the
 * top most opened popover
 * @param isOpen
 * @param priority - sets the priority for the focus trap when multiple popovers are open, similar to z-index. Popovers with a higher priority value will receive focus before others
 * @returns
 */
export const useFocusManagement = ({
  isOpen,
  priority,
}: Pick<FocusManagementType, 'isOpen'> & {priority?: number}) => {
  const popoverRef = useRef<HTMLElement | null>(null);
  const {addPopover, topMostPopover, removePopover} = usePopoverStack();

  const isTopMostPopover = useMemo(
    () => popoverRef.current === topMostPopover?.popoverRef,
    [topMostPopover?.popoverRef, popoverRef.current],
  );

  const focusManagementRefCallback =
    (customRef?: ForwardedRef<HTMLElement | null>) =>
    (el: HTMLElement | null) => {
      if (el) {
        popoverRef.current = el;

        if (isOpen) {
          addPopover({
            popoverRef: el as HTMLElement,
            opener: document.activeElement as HTMLElement,
            priority,
          });
        }
      }

      if (customRef) {
        if (typeof customRef === 'function') {
          customRef(el);
        } else {
          customRef.current = el;
        }
      }
    };

  useEffect(() => {
    if (popoverRef.current && !isOpen) {
      removePopover(popoverRef.current as HTMLElement, priority);
    }
  }, [popoverRef.current, isOpen]);

  useFocusTrap({
    isOpen,
    containerRef: popoverRef,
    disable: !isOpen || !isTopMostPopover,
    openerElement: topMostPopover?.opener,
  });

  return {
    isTopMostPopover,
    focusManagementRefCallback,
  };
};
